import React, { useState, useEffect, useCallback } from "react";
import {
  useParams,
  useNavigate
} from "react-router-dom";
import {
  getStudentFeeHistory,
  getStudentFeeHistoryPortal,
  getFeeReceipt,
  getFeeReceiptPortal
} from "../../api/fees/feeApi";
import FeeReceiptModal from "../../components/fees/FeeReceiptModal";
import {
  Button,
  Card,
  CardContent,
  Table,
  EmptyState,
  Skeleton
} from "../../components/ui";
import {
  ArrowLeft,
  History
} from "lucide-react";
import {
  useToast
} from "../../context/ToastContext";

const StudentFeeHistory = ({ isPortal = false }) => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [loadingReceipt, setLoadingReceipt] = useState(false);

  const toast = useToast();

  const fetchData = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const apiFn = isPortal ? getStudentFeeHistoryPortal : getStudentFeeHistory;
      const res = await apiFn(studentId);
      setData(res.data || res || null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to load fee history.");
    } finally {
      setLoading(false);
    }
  }, [studentId, isPortal]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const viewReceipt = async (receiptId) => {
    setLoadingReceipt(true);
    try {
      const apiFn = isPortal ? getFeeReceiptPortal : getFeeReceipt;
      const res = await apiFn(receiptId);
      setReceiptData(res.data || res);
      setReceiptOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to load receipt.");
    } finally {
      setLoadingReceipt(false);
    }
  };

  const student = data?.student || data?.studentId || {};
  const history = data?.history || data?.receipts || data?.payments || [];
  const pending = data?.pending || data?.dues || [];
  const balance = Number(data?.balance || data?.totalDue || 0);

  const pendingColumns = [
    {
      key: "feeHead",
      header: "Fee head",
      render: (d) => d.feeHeadName || d.feeHeadId?.name || d.name || "—",
    },
    {
      key: "amount",
      header: "Amount",
      render: (d) => `₹${Number(d.amount || 0).toFixed(2)}`,
    },
    {
      key: "balance",
      header: "Balance",
      render: (d) => (
        <span className="font-medium text-danger-500">
          ₹{Number(d.balance || d.amount || 0).toFixed(2)}
        </span>
      ),
    },
    {
      key: "dueDate",
      header: "Due date",
      render: (d) =>
        d.dueDate ? new Date(d.dueDate).toLocaleDateString("en-IN") : "—",
    },
  ];

  const historyColumns = [
    {
      key: "receiptNo",
      header: "Receipt no.",
      render: (h) => (
        <span className="font-medium">{h.receiptNo || h._id}</span>
      ),
    },
    {
      key: "date",
      header: "Date",
      render: (h) =>
        h.paymentDate || h.createdAt
          ? new Date(h.paymentDate || h.createdAt).toLocaleDateString("en-IN")
          : "—",
    },
    {
      key: "amount",
      header: "Amount paid",
      render: (h) =>
        `₹${Number(h.amountPaid || h.totalPaid || 0).toFixed(2)}`,
    },
    {
      key: "mode",
      header: "Mode",
      render: (h) => h.paymentMode || "—",
    },
    {
      key: "action",
      header: "",
      render: (h) => (
        <Button
          variant="ghost"
          size="sm"
          disabled={loadingReceipt}
          onClick={() => viewReceipt(h._id)}
        >
          View receipt
        </Button>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <Button
        variant="ghost"
        size="sm"
        leftIcon={<ArrowLeft className="h-4 w-4" />}
        onClick={() => navigate(-1)}
      >
        Back
      </Button>

      <div>
        <h1 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">
          Student fee history
        </h1>
        {(student.name || student.fullName) && (
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            {student.name || student.fullName} · Roll:{" "}
            {student.rollNo || student.admissionNo || "—"} · Class:{" "}
            {student.className || student.classId?.name || "—"}
          </p>
        )}
      </div>

      

      {loading ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="rounded-card border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4 shadow-card dark:shadow-black/20"
              >
                <Skeleton height={12} width="40%" className="mb-3" />
                <Skeleton height={28} width="50%" />
              </div>
            ))}
          </div>
          <div className="space-y-3 rounded-card border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4 shadow-card dark:shadow-black/20">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex gap-4">
                <Skeleton height={16} width="25%" />
                <Skeleton height={16} width="20%" />
                <Skeleton height={16} width="15%" />
                <Skeleton height={16} width="20%" />
              </div>
            ))}
          </div>
        </div>
      ) : !data ? (
        <EmptyState
          icon={<History />}
          title="No fee records found"
          description="This student has no fee history yet."
        />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Card>
              <CardContent className="pt-4">
                <p className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                  Total balance
                </p>
                <p className="mt-1 text-2xl font-semibold text-danger-500">
                  ₹{balance.toFixed(2)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                  Pending items
                </p>
                <p className="mt-1 text-2xl font-semibold text-neutral-900 dark:text-neutral-100">
                  {pending.length}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                  Receipts
                </p>
                <p className="mt-1 text-2xl font-semibold text-neutral-900 dark:text-neutral-100">
                  {history.length}
                </p>
              </CardContent>
            </Card>
          </div>

          {pending.length > 0 && (
            <div>
              <h2 className="mb-3 text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                Pending dues
              </h2>
              <Table columns={pendingColumns} data={pending} />
            </div>
          )}

          <div>
            <h2 className="mb-3 text-sm font-semibold text-neutral-900 dark:text-neutral-100">
              Payment history
            </h2>
            <Table
              columns={historyColumns}
              data={history}
              emptyState={
                <EmptyState
                  title="No payments yet"
                  description="Payments will appear here once collected."
                />
              }
            />
          </div>
        </>
      )}

      <FeeReceiptModal
        isOpen={receiptOpen}
        onClose={() => setReceiptOpen(false)}
        receipt={receiptData}
      />
    </div>
  );
};

export default StudentFeeHistory;
